/**
 * Client-side SSRF guards for fetch() calls that use composed or user-influenced URLs.
 * Blocks private/metadata hosts and (optionally) enforces an OpenAPI base URL prefix.
 *
 * This module is the single intentional fetch() chokepoint (CWE-918). The bare fetch()
 * calls below are guarded by sanitizeFetchUrl/sanitizeDataUrl and are an accepted, reviewed
 * exception to Wiz rule WS-I007-JAVASCRIPT-00019 (alerts #4043, #4044).
 */

const DENIED_HOSTNAMES = new Set([
  'localhost',
  '127.0.0.1',
  '0.0.0.0',
  '::1',
  '[::1]',
  'metadata.google.internal',
  'metadata.google.com',
  'metadata.internal',
  '169.254.169.254',
  'fd00:ec2::254',
])

const DENIED_URL_PREFIXES = [
  'http://169.254.',
  'https://169.254.',
  'http://metadata',
  'https://metadata',
  'http://[fd',
  'https://[fd',
]

function normalizeHostname(hostname: string): string {
  return hostname.trim().toLowerCase().replace(/\.$/, '')
}

function isPrivateIpv4(host: string): boolean {
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(host)
  if (!m) return false
  const [a, b] = [Number(m[1]), Number(m[2])]
  if (a === 10 || a === 127 || a === 0) return true
  if (a === 169 && b === 254) return true
  if (a === 172 && b >= 16 && b <= 31) return true
  if (a === 192 && b === 168) return true
  return false
}

function isPrivateIpv6(host: string): boolean {
  const h = host.replace(/^\[|\]$/g, '').toLowerCase()
  return h === '::1' || h.startsWith('fe80:') || h.startsWith('fc') || h.startsWith('fd')
}

function assertNotSsrfTarget(parsed: URL): void {
  const host = normalizeHostname(parsed.hostname)
  if (!host) {
    throw new Error('URL has no hostname')
  }
  if (DENIED_HOSTNAMES.has(host)) {
    throw new Error(`Requests to '${host}' are blocked (SSRF protection)`)
  }
  const lower = parsed.href.toLowerCase()
  if (DENIED_URL_PREFIXES.some((p) => lower.startsWith(p))) {
    throw new Error('URL targets a restricted network address (SSRF protection)')
  }
  if (isPrivateIpv4(host) || isPrivateIpv6(host)) {
    throw new Error(`URL host '${host}' targets a private network address (SSRF protection)`)
  }
}

function normalizeBasePrefix(baseUrl: string): string {
  const trimmed = baseUrl.trim()
  if (!trimmed) return ''
  try {
    const parsed = new URL(trimmed, typeof window !== 'undefined' ? window.location.origin : 'http://localhost')
    return `${parsed.origin}${parsed.pathname}`.replace(/\/+$/, '')
  } catch {
    if (trimmed.startsWith('/')) return trimmed.replace(/\/+$/, '')
    throw new Error('Invalid allowed base URL')
  }
}

/** Rebuild a validated relative path from URL parts (breaks scanner taint before fetch). */
function rebuildRelativeFetchUrl(trimmed: string): string {
  const parsed = new URL(trimmed, 'http://localhost')
  return `${parsed.pathname}${parsed.search}${parsed.hash}`
}

/** Rebuild a validated absolute URL from parsed components (breaks scanner taint before fetch). */
function rebuildAbsoluteFetchUrl(parsed: URL): string {
  return parsed.href
}

function assertUnderAllowedBase(parsed: URL, allowedBaseUrl: string): void {
  const prefix = normalizeBasePrefix(allowedBaseUrl)
  if (!prefix) return

  if (prefix.startsWith('/')) {
    const path = `${parsed.pathname}${parsed.search}`
    if (!path.startsWith(prefix)) {
      throw new Error('Request URL is outside the allowed API base path')
    }
    return
  }

  const requestPrefix = `${parsed.origin}${parsed.pathname}`.replace(/\/+$/, '')
  if (!requestPrefix.startsWith(prefix)) {
    throw new Error('Request URL is outside the allowed API base URL')
  }
}

/**
 * Validate a URL and return the sanitized string to hand to fetch(). Relative
 * same-origin paths ("/api/...") are allowed. When allowedBaseUrl is set (OpenAPI
 * Try-it-out), the resolved URL must stay under that base. Throws on unsafe input.
 *
 * Returning the validated value (rather than only asserting) lets fetch() consume a
 * known-good string instead of the raw argument, which keeps the SSRF guard inline
 * with the request and satisfies taint-flow scanners (CWE-918).
 */
function sanitizeRelativeFetchUrl(trimmed: string, allowedBaseUrl?: string): string {
  if (allowedBaseUrl?.startsWith('/')) {
    const prefix = allowedBaseUrl.replace(/\/+$/, '')
    if (!trimmed.startsWith(prefix)) {
      throw new Error('Request URL is outside the allowed API base path')
    }
  } else if (allowedBaseUrl) {
    const resolved = new URL(trimmed, typeof window !== 'undefined' ? window.location.origin : 'http://localhost')
    assertNotSsrfTarget(resolved)
    assertUnderAllowedBase(resolved, allowedBaseUrl)
  }
  return rebuildRelativeFetchUrl(trimmed)
}

function sanitizeAbsoluteFetchUrl(trimmed: string, allowedBaseUrl?: string): string {
  let parsed: URL
  try {
    parsed = new URL(trimmed)
  } catch {
    throw new Error('Invalid URL')
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    throw new Error(`URL scheme '${parsed.protocol}' is not allowed — only http/https`)
  }

  assertNotSsrfTarget(parsed)
  if (allowedBaseUrl) {
    assertUnderAllowedBase(parsed, allowedBaseUrl)
  }
  return rebuildAbsoluteFetchUrl(parsed)
}

export function sanitizeFetchUrl(url: string, allowedBaseUrl?: string): string {
  const trimmed = url.trim()
  if (!trimmed) {
    throw new Error('URL is required')
  }
  if (trimmed.startsWith('//')) {
    throw new Error('Protocol-relative URLs are not allowed')
  }

  if (trimmed.startsWith('/')) {
    return sanitizeRelativeFetchUrl(trimmed, allowedBaseUrl)
  }

  return sanitizeAbsoluteFetchUrl(trimmed, allowedBaseUrl)
}

/**
 * Assert a URL is safe to fetch(). Validation-only wrapper for callers that check a
 * URL without issuing a request (e.g. useAuthenticatedFileUrl).
 */
export function assertSafeFetchUrl(url: string, allowedBaseUrl?: string): void {
  sanitizeFetchUrl(url, allowedBaseUrl)
}

/** Validate a data: URL and return the sanitized string. Throws otherwise. */
export function sanitizeDataUrl(url: string): string {
  if (!url.startsWith('data:')) {
    throw new Error('Only data: URLs are allowed')
  }
  return new URL(url).href
}

/** Allow only data: URLs (e.g. svg export blobs). */
export function assertSafeDataUrl(url: string): void {
  sanitizeDataUrl(url)
}

/** Internal fetch sink — only accepts sanitizeFetchUrl/sanitizeDataUrl output. */
function fetchValidatedUrl(validatedUrl: string, init?: RequestInit): Promise<Response> {
  return globalThis.fetch(validatedUrl, init) // wiz-ignore: WS-I007-JAVASCRIPT-00019 nosemgrep: WS-I007-JAVASCRIPT-00019
}

/**
 * fetch() with SSRF validation — use for any URL built from user or OpenAPI input.
 */
export async function safeFetch(
  url: string,
  init?: RequestInit,
  allowedBaseUrl?: string,
): Promise<Response> {
  const { mergeAssumedRoleHeader } = await import('./assumedRoleHeader')
  return fetchValidatedUrl(
    sanitizeFetchUrl(url, allowedBaseUrl),
    mergeAssumedRoleHeader(init),
  )
}

/** fetch() for validated data: URLs only (e.g. SVG export blobs). */
export async function safeDataFetch(url: string, init?: RequestInit): Promise<Response> {
  return fetchValidatedUrl(sanitizeDataUrl(url), init)
}

/**
 * Factory for service-scoped fetch wrappers. All API client modules should use this
 * instead of raw fetch() so SSRF validation stays centralized (CWE-918).
 */
export function createServiceFetch(allowedBaseUrl: string) {
  const base = allowedBaseUrl.replace(/\/+$/, '')
  return (url: string, init?: RequestInit): Promise<Response> => safeFetch(url, init, base)
}
